import { Injectable, inject } from '@angular/core';

import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  catchError,
  debounce,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
  timer
} from 'rxjs';

import { SearchAutoSuggestions } from '@hdms/shared/common-types';
import {
  GQL_DATASOURCE_VERSION_HEADER,
  SEARCH_SERVICE,
  UserContextService,
  getLocalization
} from '@hdms/shared/data-access';
import { BrowserService, EnvironmentService, FeatureToggleService } from '@hdms/shared/util-core';

import { HeaderAutoSuggestGqlService } from './header-auto-suggest-gql.service';
import { OPENSEARCH_AUTOSUGGEST_CATEGORIES_TOGGLE, URLS } from './header-urls.const';

interface AutoSuggestionsPlusTerm extends Partial<SearchAutoSuggestions> {
  term: string;
}

export interface ExtendedAutoSuggestions extends AutoSuggestionsPlusTerm {
  hasSuggestions: boolean;
  hasValidTerm: boolean;
  searchUrl: string;
}

const SEARCH_CHANGE_DEBOUNCE_TIME = 300;
const SUGGESTION_DEFAULT_LN_LIMIT = 5;
const SEARCH_TERM_MIN_LN = 2;

function isValidSearchTerm(term: string): boolean {
  return term?.length >= SEARCH_TERM_MIN_LN;
}

function hasSuggestions({ categories, products }: AutoSuggestionsPlusTerm): boolean {
  return !!categories?.length || !!products?.length;
}

export function getSearchUrl(term: string): string {
  return term && `${URLS.search}${encodeURIComponent(term)}`;
}

export function extendSuggestionData(data: AutoSuggestionsPlusTerm): ExtendedAutoSuggestions {
  const { term } = data;
  return {
    ...data,
    hasSuggestions: hasSuggestions(data),
    hasValidTerm: isValidSearchTerm(term),
    searchUrl: getSearchUrl(term)
  };
}

@Injectable()
export class SearchSuggestionService {
  public readonly suggestions$: Observable<ExtendedAutoSuggestions>;

  private readonly searchTerm$ = new ReplaySubject<string>(1);
  private readonly loading$ = new BehaviorSubject<boolean>(false);

  private readonly autoSuggestGqlService = inject(HeaderAutoSuggestGqlService);
  private readonly userContext = inject(UserContextService);
  private readonly envService = inject(EnvironmentService);
  private readonly browserService = inject(BrowserService);
  private readonly ftService = inject(FeatureToggleService);
  constructor() {
    this.suggestions$ = this.getSuggestions$();
  }

  isBarcodeSupported(): boolean {
    return this.browserService.isMobile;
  }

  getSearchTerm$(): Observable<string> {
    return this.searchTerm$.asObservable();
  }

  isLoading$(): Observable<boolean> {
    return this.loading$.asObservable();
  }

  updateSearchTerm(term: string): void {
    this.searchTerm$.next(term);
    this.loading$.next(isValidSearchTerm(term));
  }

  private getSuggestions$(): Observable<ExtendedAutoSuggestions> {
    return this.searchTerm$.pipe(
      debounce(term => timer(isValidSearchTerm(term) ? SEARCH_CHANGE_DEBOUNCE_TIME : 0)),
      switchMap(term => this.fetchSuggestions$(term).pipe(map(data => ({ ...data, term })))),
      map(extendSuggestionData),
      tap(_ => this.loading$.next(false)),
      shareReplay(1)
    );
  }

  private fetchSuggestions$(query: string): Observable<SearchAutoSuggestions> {
    const openSearchToggleEnabled = this.ftService.getToggleById(OPENSEARCH_AUTOSUGGEST_CATEGORIES_TOGGLE);
    return isValidSearchTerm(query)
      ? this.autoSuggestGqlService
          .fetch(
            {
              filter: {
                localization: getLocalization(this.envService),
                includeSpecialProducts: this.userContext.isAccountManager,
                limit: SUGGESTION_DEFAULT_LN_LIMIT,
                query
              }
            },
            openSearchToggleEnabled && { context: { headers: { [GQL_DATASOURCE_VERSION_HEADER]: SEARCH_SERVICE } } }
          )
          .pipe(
            map(({ data }) => data.searchAutoSuggestions),
            catchError(_ => of(null))
          )
      : of(null);
  }
}
